import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Calendar, MapPin, Clock, Loader2 } from 'lucide-react';
import { apiService } from '../services/api';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/uploads/')) return url;

  const normalizedPath = url.startsWith('/') ? url : `/${url}`;
  const baseUrl = (BACKEND_URL || '').replace(/\/api\/?$/, '');
  if (!baseUrl) return normalizedPath;
  return `${baseUrl}${normalizedPath}`;
};

const TeamBadge = ({ teamName, logo }) => {
  if (logo) {
    return (
      <img
        src={logo}
        alt={`Logo ${teamName}`}
        className="w-12 h-12 rounded-full border-2 border-green-200 object-cover bg-white shadow-sm"
        loading="lazy"
      />
    );
  }

  return (
    <div className="w-12 h-12 rounded-full border-2 border-green-200 bg-green-100 text-green-700 flex items-center justify-center font-bold text-sm shadow-sm">
      {teamName?.split(' ').slice(0, 2).map((word) => word.charAt(0)).join('') || '?'}
    </div>
  );
};

const JadwalHasil = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedEventId = searchParams.get('eventId');
  const [filter, setFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [matches, setMatches] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState(['U-8', 'U-10', 'U-12', 'U-14']);
  const [teamLogos, setTeamLogos] = useState({});
  const [registeredTeams, setRegisteredTeams] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    if (!selectedEventId) {
      navigate(`/event?next=${encodeURIComponent(`${location.pathname}${location.search}`)}`, { replace: true });
    }
  }, [selectedEventId, navigate, location.pathname, location.search]);

  useEffect(() => {
    const status = searchParams.get('status');
    if (status === 'scheduled' || status === 'finished') {
      setFilter(status);
      return;
    }
    setFilter('all');
  }, [searchParams]);

  useEffect(() => {
    if (!selectedEventId) return;
    loadData();
  }, [selectedEventId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [matchesRes, participantsRes] = await Promise.all([
        apiService.getMatches(null, null, selectedEventId),
        apiService.getParticipants(),
      ]);

      const fetchedMatches = matchesRes.data || [];
      const categories = Array.from(new Set(fetchedMatches.map((m) => m.category).filter(Boolean)));
      if (categories.length > 0) {
        setCategoryOptions(categories);
      }
      const participants = participantsRes.data || [];
      const logoMap = {};
      const nameSet = new Set();

      participants.forEach((team) => {
        const key = (team?.name || '').trim().toLowerCase();
        if (!key) return;
        logoMap[key] = getImageUrl(team.logo);
        nameSet.add(key);
      });

      setMatches(fetchedMatches);
      setTeamLogos(logoMap);
      setRegisteredTeams(nameSet);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Gagal memuat jadwal pertandingan.',
        variant: 'destructive',
      });
      setMatches([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredMatches = useMemo(() => matches.filter((match) => {
    const statusMatch = filter === 'all' || match.status === filter;
    const categoryMatch = categoryFilter === 'all' || match.category === categoryFilter;
    const homeKey = (match.home_team || match.homeTeam || '').trim().toLowerCase();
    const awayKey = (match.away_team || match.awayTeam || '').trim().toLowerCase();
    const bothRegistered = homeKey && awayKey &&
      registeredTeams.has(homeKey) && registeredTeams.has(awayKey);
    return statusMatch && categoryMatch && bothRegistered;
  }), [matches, filter, categoryFilter, registeredTeams]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-green-600" />
      </div>
    );
  }

  if (!selectedEventId) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Jadwal & Hasil Pertandingan</h1>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status Pertandingan
              </label>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="all">Semua</option>
                <option value="scheduled">Akan Datang</option>
                <option value="finished">Selesai</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Kategori
              </label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="all">Semua Kategori</option>
                {categoryOptions.map((category) => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Matches List */}
        <div className="space-y-4">
          {filteredMatches.map((match) => {
            const homeTeam = match.home_team || match.homeTeam || '-';
            const awayTeam = match.away_team || match.awayTeam || '-';
            const homeScore = match.home_score ?? match.homeScore;
            const awayScore = match.away_score ?? match.awayScore;
            const matchDate = match.date;
            const homeLogo = teamLogos[homeTeam.trim().toLowerCase()] || '';
            const awayLogo = teamLogos[awayTeam.trim().toLowerCase()] || '';

            return (
            <div key={match.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-200">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                    {match.category}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    match.status === 'finished'
                      ? 'bg-gray-100 text-gray-800'
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {match.status === 'finished' ? 'Selesai' : 'Akan Datang'}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-center">
                  {/* Home Team */}
                  <div className="md:col-span-2 flex items-center justify-center md:justify-end gap-3 text-center md:text-right">
                    <h3 className="text-xl font-bold text-gray-900">{homeTeam}</h3>
                    <TeamBadge teamName={homeTeam} logo={homeLogo} />
                  </div>

                  {/* Score */}
                  <div className="text-center">
                    {match.status === 'finished' ? (
                      <div className="flex items-center justify-center space-x-4">
                        <span className="text-3xl font-bold text-gray-900">{homeScore}</span>
                        <span className="text-2xl font-bold text-gray-400">-</span>
                        <span className="text-3xl font-bold text-gray-900">{awayScore}</span>
                      </div>
                    ) : (
                      <div className="text-2xl font-bold text-gray-400">VS</div>
                    )}
                  </div>

                  {/* Away Team */}
                  <div className="md:col-span-2 flex items-center justify-center md:justify-start gap-3 text-center md:text-left">
                    <TeamBadge teamName={awayTeam} logo={awayLogo} />
                    <h3 className="text-xl font-bold text-gray-900">{awayTeam}</h3>
                  </div>
                </div>

                {/* Goal Events */}
                {match.status === 'finished' && match.goal_events && match.goal_events.length > 0 && (() => {
                  const homeGoals = match.goal_events.filter((e) => e.team === 'home');
                  const awayGoals = match.goal_events.filter((e) => e.team === 'away');
                  return (
                    <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 gap-4 text-sm">
                      {/* Home scorers */}
                      <div className="space-y-1">
                        {homeGoals.map((e, i) => (
                          <div key={i} className="text-gray-600">
                            <span className="font-medium text-gray-800">
                              {e.type === 'own_goal' ? `${e.player_name} (OG)` : e.player_name}
                            </span>
                            {e.minute ? <span className="text-xs text-gray-400"> {e.minute}'</span> : null}
                            {e.assist_by ? <span className="text-xs text-gray-400"> (A: {e.assist_by})</span> : null}
                          </div>
                        ))}
                      </div>
                      {/* Away scorers */}
                      <div className="space-y-1 text-right">
                        {awayGoals.map((e, i) => (
                          <div key={i} className="text-gray-600">
                            {e.assist_by ? <span className="text-xs text-gray-400">(A: {e.assist_by}) </span> : null}
                            {e.minute ? <span className="text-xs text-gray-400">{e.minute}' </span> : null}
                            <span className="font-medium text-gray-800">
                              {e.type === 'own_goal' ? `${e.player_name} (OG)` : e.player_name}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Match Info */}
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4 text-green-600" />
                      <span>{new Date(matchDate).toLocaleDateString('id-ID', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Clock className="h-4 w-4 text-green-600" />
                      <span>{match.time} WIB</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <MapPin className="h-4 w-4 text-green-600" />
                      <span>{match.venue}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )})}
        </div>

        {filteredMatches.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">Tidak ada pertandingan ditemukan.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default JadwalHasil;